import React, { useState } from 'react'
import { 
  Calendar, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle, 
  XCircle, 
  TrendingUp
} from 'lucide-react'
import { cn } from '@/lib/utils'

export const AgencySessionsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [timeFilter, setTimeFilter] = useState<string>('all')

  // Mock sessions data - in real implementation, this would come from API
  const sessions = [
    {
      id: '1',
      studentName: 'Sarah Johnson',
      counselorName: 'Dr. Emily Rodriguez',
      type: 'Career Counseling',
      status: 'completed',
      scheduledDate: '2024-06-20',
      scheduledTime: '10:00 AM',
      duration: 60,
      rating: 5,
      notes: 'Discussed career paths in engineering',
    },
    {
      id: '2',
      studentName: 'Michael Chen',
      counselorName: 'Prof. Sarah Wilson',
      type: 'Academic Planning',
      status: 'scheduled',
      scheduledDate: '2024-06-21',
      scheduledTime: '2:00 PM',
      duration: 45,
      rating: null,
      notes: null,
    },
    {
      id: '3',
      studentName: 'Emily Davis',
      counselorName: 'Dr. James Anderson',
      type: 'College Admissions',
      status: 'in_progress',
      scheduledDate: '2024-06-21',
      scheduledTime: '11:00 AM',
      duration: 60,
      rating: null,
      notes: null,
    },
    {
      id: '4',
      studentName: 'David Wilson',
      counselorName: 'Dr. Emily Rodriguez',
      type: 'Personal Development',
      status: 'cancelled',
      scheduledDate: '2024-06-19',
      scheduledTime: '3:00 PM',
      duration: 45,
      rating: null,
      notes: 'Student cancelled due to illness',
    },
    {
      id: '5',
      studentName: 'Lisa Thompson',
      counselorName: 'Prof. Sarah Wilson',
      type: 'Career Counseling',
      status: 'completed',
      scheduledDate: '2024-06-19',
      scheduledTime: '9:00 AM',
      duration: 60,
      rating: 4,
      notes: 'Explored options in healthcare field',
    },
  ]

  const sessionTypes = ['All Types', 'Career Counseling', 'Academic Planning', 'College Admissions', 'Personal Development']

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         session.counselorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         session.type.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter
    const matchesTime = timeFilter === 'all' || 
                       (timeFilter === 'today' && session.scheduledDate === '2024-06-21') ||
                       (timeFilter === 'week' && new Date(session.scheduledDate) >= new Date('2024-06-15'))
    return matchesSearch && matchesStatus && matchesTime
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'scheduled':
        return 'bg-blue-100 text-blue-800'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 mr-1" />
      case 'scheduled':
        return <Calendar className="h-3 w-3 mr-1" />
      case 'in_progress':
        return <Clock className="h-3 w-3 mr-1" />
      case 'cancelled':
        return <XCircle className="h-3 w-3 mr-1" />
      default:
        return null
    }
  }

  const completedSessions = sessions.filter(s => s.status === 'completed').length
  const inProgressSessions = sessions.filter(s => s.status === 'in_progress').length
  const avgRating = sessions.filter(s => s.rating).reduce((sum, s) => sum + (s.rating || 0), 0) / sessions.filter(s => s.rating).length

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sessions</h1>
          <p className="text-gray-600">Monitor and manage counseling sessions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{completedSessions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">{inProgressSessions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Rating</p>
                <p className="text-2xl font-bold text-gray-900">{avgRating.toFixed(1)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search sessions by student, counselor, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              
              <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </button>
            </div>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Sessions ({filteredSessions.length})
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Session #{session.id}
                        </div>
                        {session.notes && (
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {session.notes}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {session.studentName}
                        </div>
                        <div className="text-sm text-gray-500">
                          with {session.counselorName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{session.type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {new Date(session.scheduledDate).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.scheduledTime}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{session.duration} min</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                        getStatusColor(session.status)
                      )}>
                        {getStatusIcon(session.status)}
                        {session.status.charAt(0).toUpperCase() + session.status.slice(1).replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {session.rating ? (
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">{session.rating}</span>
                          <span className="text-yellow-400 ml-1">★</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Insights */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Session Types Distribution</h4>
            <div className="space-y-3">
              {sessionTypes.slice(1).map((type) => {
                const count = sessions.filter(s => s.type === type).length
                const percentage = (count / sessions.length) * 100
                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{type}</span>
                      <span className="font-medium">{count} ({percentage.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Success Rate</h4>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {Math.round((completedSessions / sessions.length) * 100)}%
              </div>
              <p className="text-gray-600">of sessions completed successfully</p>
              <div className="mt-4 text-sm text-gray-500">
                {completedSessions} completed out of {sessions.length} total
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Sessions</h4>
            <div className="space-y-3">
              {sessions.filter(s => s.status === 'scheduled').slice(0, 3).map((session) => (
                <div key={session.id} className="flex justify-between items-center">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{session.studentName}</div>
                    <div className="text-xs text-gray-500">{session.type}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-900">{session.scheduledTime}</div>
                    <div className="text-xs text-gray-500">{new Date(session.scheduledDate).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}